import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { ChevronDown } from "lucide-react";
import { useGlossaryBlueprintsData } from "../../lib/useTemplatesData";
import { useSelectedGameUsers } from "../../lib/useSelectedGameUsers";
import { useGlossaryState } from "../wizards/glossary/useObjectState";
import { GlossaryBlockForm } from "../wizards/glossary/ObjectBlockForm";
import { UnifiedWizardHeader } from "../wizards/shared/UnifiedWizardHeader";
import { useToast } from "../../hooks/use-toast";
import { useConfirmation } from "../../hooks/use-confirmation";
import {
  createObject,
  updateObject,
  deleteObject,
  objectsStore,
} from "../../lib/stores/objectsStore";
import { useGlossaryData } from "@/lib/useObjectsData";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface DocumentBlockProps {
  id?: string;
  content?: string;
  isActive?: boolean;
  onContentChange?: (id: string, content: string) => void;
  onAddBlock?: (id: string) => void;
  initialData?: any; // For editing existing document items
  onSave?: (documentItem: {
    id?: string;
    game_id?: string;
    name: string;
    tags: string[];
    data: any[];
    blueprint_id?: string | null;
    version?: {
      changes: Record<string, unknown>;
      version: number;
    };
    blueprint_version?: number;
  }) => void;
  onUpdate?: (documentItem: {
    id?: string;
    game_id?: string;
    name: string;
    tags: string[];
    data: any[];
    blueprint_id?: string | null;
    version?: {
      changes: Record<string, unknown>;
      version: number;
    };
    blueprint_version?: number;
  }) => void;
  // New props for document integration
  onDocumentContentUpdate?: (
    blockId: string,
    glossaryItemId: string,
    glossaryItemName: string
  ) => void;
  onDocumentSave?: () => void;
  onDocumentBlockDelete?: (blockId: string) => void;
  onDocumentBlockCancel?: (blockId: string) => void;
}

type DocumentMode = "create" | "edit" | "view";

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const DocumentBlock = ({
  id,
  content,
  initialData,
  onDocumentContentUpdate,
  onDocumentSave,
  onDocumentBlockDelete,
  onDocumentBlockCancel,
  onSave,
  onUpdate,
}: DocumentBlockProps) => {
  const { t } = usePlatformTranslation();
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  // Initialize mode based on whether we have existing content or initial data
  const getInitialMode = (): DocumentMode => {
    if (content || initialData) {
      return "view";
    }
    return "create";
  };
  const [documentMode, setDocumentMode] = useState<DocumentMode>(
    getInitialMode()
  );
  const [isManualModeChange, setIsManualModeChange] = useState(false);

  // Ref to track current mode without causing useEffect re-runs
  const currentModeRef = useRef<DocumentMode>(getInitialMode());

  // Ref to track if we've already processed edit mode data
  const hasProcessedEditModeRef = useRef(false);

  // Update ref whenever mode changes
  useEffect(() => {
    currentModeRef.current = documentMode;
  }, [documentMode]);

  // Track if we've already loaded glossary data to prevent infinite calls
  const hasLoadedGlossaryData = useRef(false);

  // Track previous values to prevent unnecessary updates
  const prevTitleRef = useRef<string>("");
  const prevBlueprintIdRef = useRef<string>("");

  // ============================================================================
  // HOOKS & DATA
  // ============================================================================

  const { selectedGame } = useSelectedGameUsers();
  const gameId = selectedGame?.id;
  const { toast } = useToast();
  const { confirmation } = useConfirmation();

  console.log(`[DocumentBlock] Component rendered:`, { id, gameId });

  // Track component lifecycle
  useEffect(() => {
    console.log(`[DocumentBlock] Component mounted:`, { id, gameId });
    return () => {
      console.log(`[DocumentBlock] Component unmounting:`, { id, gameId });
    };
  }, [id, gameId]);

  // Instead of useGlossaryDataContext, use a document-specific hook
  const { items: documentItems, loadData: loadDocumentData } = useGlossaryData({
    gameId,
    type: "document",
    autoLoad: false, // Disable auto-loading to prevent unnecessary API calls
  });

  // Document blueprints data
  const { getBlueprintDocuments, isLoading } = useGlossaryBlueprintsData({
    gameId,
  });


  // Ensure glossary data is loaded when component mounts and we have a gameId
  useEffect(() => {
    console.log(`[DocumentBlock] useEffect triggered:`, { 
      gameId, 
      hasLoadedGlossaryData: hasLoadedGlossaryData.current, 
      isLoading,
      documentItemsLength: documentItems?.length,
      content,
      documentMode
    });
    
    if (gameId && !hasLoadedGlossaryData.current && !isLoading) {
      console.log(`[DocumentBlock] Loading glossary data for gameId: ${gameId}`);
      hasLoadedGlossaryData.current = true;
      loadDocumentData();
    } else {
      console.log(`[DocumentBlock] Skipping data load:`, { 
        reason: !gameId ? 'no gameId' : 
                hasLoadedGlossaryData.current ? 'already loaded' : 
                isLoading ? 'already loading' : 'unknown'
      });
    }
  }, [gameId, loadDocumentData, isLoading, documentItems?.length, content, documentMode]);

  // Add debugging for document data
  

  // Get available document blueprints for the current game - memoized to prevent unnecessary calls
  const documentBlueprints = useMemo(() => {
    return gameId ? getBlueprintDocuments() : [];
  }, [gameId, getBlueprintDocuments]);

  // Filter blueprints to only show those with type "document" - memoized to prevent unnecessary filtering
  const filteredBlueprints = useMemo(() => {
    return documentBlueprints.filter(
      (blueprint) => blueprint.type === "document"
    );
  }, [documentBlueprints]);

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  /**
   * Gets the blueprint by ID for the initial data if editing
   */
  const getBlueprintById = useCallback(
    (id: string) => {
      return filteredBlueprints.find((bp) => bp.id === id) || null;
    },
    [filteredBlueprints]
  );

  /**
   * Gets the glossary item data from the block content
   * This is used when the block is in view mode and contains a glossary item reference
   */
  const getGlossaryItemFromBlock = useCallback(() => {
    console.log(`[DocumentBlock] getGlossaryItemFromBlock called:`, {
      content,
      documentItemsLength: documentItems?.length,
      documentItemsIds: documentItems?.map(item => item.id) || []
    });
    
    if (!content || !documentItems) {
      console.log(`[DocumentBlock] getGlossaryItemFromBlock: No content or documentItems`);
      return null;
    }
    
    // Try to find the glossary item by ID from the block content
    const documentItem = documentItems.find((item) => item.id === content);
    console.log(`[DocumentBlock] getGlossaryItemFromBlock result:`, documentItem ? { id: documentItem.id, name: documentItem.name } : null);
    return documentItem || null;
  }, [content, documentItems]);

  /**
   * Generates the next untitled number for new document items
   */
  const generateNextUntitledNumber = useCallback(() => {
    if (!gameId) return 1;
    // For now, just return a simple increment - you can implement the full logic later
    return Math.floor(Math.random() * 1000) + 1;
  }, [gameId]);

  // Get the glossary item data if we have content but no initialData
  const documentItemFromContent = useMemo(() => {
    if (!initialData && content && documentItems) {
      return documentItems.find((item) => item.id === content) || null;
    }
    return null;
  }, [initialData, content, documentItems]);

  // Get the blueprint for the glossary item (either from initialData or from content)
  const getInitialBlueprint = useCallback(() => {
    const item = initialData || documentItemFromContent;
    if (item?.blueprint_id) {
      return filteredBlueprints.find((bp) => bp.id === item.blueprint_id) || null;
    }
    return null;
  }, [initialData, documentItemFromContent, filteredBlueprints]);

  // Custom hook for managing document state - same pattern as GlossaryWizard
  const documentState = useGlossaryState({
    initialData: initialData || documentItemFromContent,
    blueprint: getInitialBlueprint(),
  });

  /**
   * Handles deletion of the document item with confirmation
   */
  const handleDelete = () => {
    const documentItem = initialData || documentItemFromContent;
  
    
    if (!documentItem?.id || !gameId) {
      toast({
        title: "Error",
        description: "Cannot delete: Missing item ID or game ID",
        variant: "destructive",
      });
      return;
    }

    const confirmDialog = confirmation({
      title: "Delete Document",
      description: `Are you sure you want to delete "${documentItem.name || 'this document'}"? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "destructive",
      onConfirm: async () => {
        try {
          const result = await deleteObject(documentItem.id, gameId, toast);
          confirmDialog.dismiss();
          
          // Notify parent component that this block should be removed from the document
          if (id && onDocumentBlockDelete) {
            onDocumentBlockDelete(id);
          }
        } catch (error) {
          console.error("Error deleting glossary item:", error);
          confirmDialog.dismiss();
          // Error toast is already shown by deleteObject
        }
      },
      onCancel: () => {
        confirmDialog.dismiss();
      },
    });
  };

  // ============================================================================
  // EFFECTS
  // ============================================================================

  /**
   * Update title when component mounts or gameId changes
   */
  useEffect(() => {
    if (gameId && !initialData && !documentItemFromContent) {
      const nextNumber = generateNextUntitledNumber();
      const newTitle = `Untitled-${nextNumber}`;
      // Only update if the title is different to prevent unnecessary re-renders
      if (prevTitleRef.current !== newTitle) {
        prevTitleRef.current = newTitle;
        documentState.setItemTitle(newTitle);
      }
    }
  }, [
    gameId,
    initialData,
    documentItemFromContent,
    generateNextUntitledNumber,
  ]);

  /**
   * Load glossary data when component mounts if we have a glossary item reference
   * Only run once when content or gameId changes, not on every render
   */
  useEffect(() => {
    if (content && gameId && !hasLoadedGlossaryData.current) {
      // Check if we already have the specific glossary item we need
      const existingItem = documentItems.find((item) => item.id === content);
      if (existingItem) {
        // We already have the data, no need to load
        hasLoadedGlossaryData.current = true;
        return;
      }

      // Only load if we haven't already loaded glossary data
      hasLoadedGlossaryData.current = true;
      // Use a stable reference to prevent infinite calls
      const loadData = async () => {
        try {
          await loadDocumentData();
        } catch (error) {
          console.error("Failed to load document data:", error);
        }
      };
      loadData();
    }
  }, [content, gameId, documentItems, loadDocumentData]); // Add documentItems and loadDocumentData to dependencies

  /**
   * Reset the loaded flag when content changes to allow loading new glossary items
   * This ensures that when a new glossary item is saved and content changes to its ID,
   * we can load the fresh data for display
   */
  useEffect(() => {
    console.log(`[DocumentBlock] Content changed to: ${content}, resetting loaded flag`);
    console.log(`[DocumentBlock] Current documentItems count: ${documentItems?.length || 0}`);
    console.log(`[DocumentBlock] Current documentItems IDs:`, documentItems?.map(item => item.id) || []);
    
    hasLoadedGlossaryData.current = false;
    
    // If we have content (glossary item ID) but no documentItems loaded yet,
    // trigger a data load to ensure we get the fresh data
    if (content && gameId && documentItems.length === 0) {
      console.log(`[DocumentBlock] Content changed but no documentItems loaded, triggering data load`);
      const loadData = async () => {
        try {
          await loadDocumentData();
          console.log(`[DocumentBlock] Data load completed after content change`);
        } catch (error) {
          console.error("Failed to load document data after content change:", error);
        }
      };
      loadData();
    } else if (content && gameId && documentItems.length > 0) {
      // Check if we already have the item we need
      const existingItem = documentItems.find(item => item.id === content);
      if (existingItem) {
        console.log(`[DocumentBlock] Item already exists in documentItems:`, existingItem.name);
        hasLoadedGlossaryData.current = true;
      } else {
        console.log(`[DocumentBlock] Content ${content} not found in existing documentItems, triggering data load`);
        const loadData = async () => {
          try {
            await loadDocumentData();
            console.log(`[DocumentBlock] Data load completed after content change`);
          } catch (error) {
            console.error("Failed to load document data after content change:", error);
          }
        };
        loadData();
      }
    }
  }, [content, gameId, documentItems.length, loadDocumentData]);

  /**
   * Cleanup effect to reset the loaded flag when component unmounts
   */
  useEffect(() => {
    return () => {
      hasLoadedGlossaryData.current = false;
    };
  }, []);

  /**
   * Update mode based on available data - simplified like GlossaryWizard
   */
  useEffect(() => {
    console.log(`[DocumentBlock] Mode update effect triggered:`, {
      hasInitialData: !!initialData,
      hasDocumentItemFromContent: !!documentItemFromContent,
      isManualModeChange
    });
    
    // Don't override mode if user manually set it to edit
    if (isManualModeChange && currentModeRef.current === "edit") {
      console.log(`[DocumentBlock] Skipping mode override - user manually set to edit`);
      return;
    }

    if (initialData || documentItemFromContent) {
      console.log(`[DocumentBlock] Setting document mode to view`);
      setDocumentMode("view");
    } else {
      console.log(`[DocumentBlock] Setting document mode to create`);
      setDocumentMode("create");
    }
  }, [initialData, documentItemFromContent, isManualModeChange]);

  // This effect is no longer needed since useGlossaryState handles blueprint setup automatically

  // This effect is no longer needed since useGlossaryState handles section parsing automatically

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Switches document to edit mode - simplified like GlossaryWizard
   */
  const handleEdit = () => {
    console.log(`[DocumentBlock] Switching to edit mode`);
    setIsManualModeChange(true);
    setDocumentMode("edit");
  };

  /**
   * Updates the document item title
   */
  const handleTitleChange = (newTitle: string) => {
    documentState.setItemTitle(newTitle);
  };

  /**
   * Saves or updates the document item to the server and updates document content
   */
  const handleSave = async () => {
    if (!gameId || !documentState.selectedBlueprint) {
      toast({
        title: "Error",
        description: "Missing game ID or document blueprint data",
        variant: "destructive",
      });
      return;
    }

    try {
      const documentItemData = documentState.getGlossaryItemData();
      if (documentMode === "create") {
        // Create new document item
        const createdItem = await createObject(
          {
            game_id: gameId,
            name: documentItemData.name,
            type: "document", // Add type field for document items
            data: documentItemData.data,
            tags: documentItemData.tags || [],
            blueprint_id: documentItemData.blueprint_id || null,
            version: { changes: {}, version: 0.00001 },
            blueprint_version: documentItemData.blueprint_version || 1,
            status: documentItemData.status, // Include draft/published status
          },
          toast
        );

        // Show success message based on status
        toast({
          title: "Success",
          description: createdItem.status === 'draft'
            ? `Document "${createdItem.name}" saved as draft. Complete all required fields to publish.`
            : `Document "${createdItem.name}" created successfully`,
        });

        // Update the document block content with the glossary item ID
        if (id && onDocumentContentUpdate) {
          // Only pass the essential reference information, not the entire data
          onDocumentContentUpdate(id, createdItem.id, createdItem.name);
        }

        // Immediately refresh the glossary data to ensure the new item is loaded
        // This ensures the saved data is displayed in the DocumentBlock
        try {
          console.log(`[DocumentBlock] Refreshing glossary data after creating item: ${createdItem.id}`);
          await loadDocumentData();
          
          // Add a small delay and retry if the item still isn't loaded
          // This handles cases where the store update might be delayed
          setTimeout(async () => {
            const retryItem = documentItems.find((item) => item.id === createdItem.id);
            if (!retryItem) {
              console.log(`[DocumentBlock] Item not found after first load, retrying...`);
              await loadDocumentData();
              
              // Check again after retry
              setTimeout(() => {
                const finalItem = documentItems.find((item) => item.id === createdItem.id);
                if (!finalItem) {
                  console.warn(`[DocumentBlock] Item still not found after retry, forcing store refresh`);
                  // Force a complete store refresh by clearing cache and reloading
                  objectsStore.clearCache().then(() => loadDocumentData());
                }
              }, 100);
            }
          }, 100);
        } catch (error) {
          console.error("Failed to refresh glossary data after save:", error);
        }

        // Document save is now handled by the parent component after block content update
        // No need to call onDocumentSave here as it will be called after onDocumentContentUpdate

        // Call the onSave callback if provided
        onSave?.(createdItem);
      } else if (documentMode === "edit") {
        // Update existing document item
        if (!documentItem?.id) {
          toast({
            title: "Error",
            description: "Missing document ID for update",
            variant: "destructive",
          });
          return;
        }

        const updatedItem = await updateObject(
          {
            id: documentItem.id,
            game_id: gameId,
            name: documentItemData.name,
            type: "document", // Add type field for document items
            data: documentItemData.data,
            tags: documentItemData.tags || [],
            blueprint_id: documentItemData.blueprint_id || null,
            version: { changes: {}, version: 0.00001 },
            blueprint_version: documentItemData.blueprint_version || 1,
            status: documentItemData.status, // Include draft/published status
          },
          'patch',
          undefined,
          toast
        );

        // Show success message based on status
        toast({
          title: "Success",
          description: updatedItem.status === 'draft'
            ? `Document "${updatedItem.name}" saved as draft. Complete all required fields to publish.`
            : `Document "${updatedItem.name}" updated successfully`,
        });

        // Immediately refresh the glossary data to ensure the updated item is loaded
        // This ensures the saved data is displayed in the DocumentBlock
        try {
          console.log(`[DocumentBlock] Refreshing glossary data after updating item: ${updatedItem.id}`);
          await loadDocumentData();
          
          // Add a small delay and retry if the item still isn't loaded
          // This handles cases where the store update might be delayed
          setTimeout(async () => {
            const retryItem = documentItems.find((item) => item.id === updatedItem.id);
            if (!retryItem) {
              console.log(`[DocumentBlock] Item not found after first load, retrying...`);
              await loadDocumentData();
              
              // Check again after retry
              setTimeout(() => {
                const finalItem = documentItems.find((item) => item.id === updatedItem.id);
                if (!finalItem) {
                  console.warn(`[DocumentBlock] Item still not found after retry, forcing store refresh`);
                  // Force a complete store refresh by clearing cache and reloading
                  objectsStore.clearCache().then(() => loadDocumentData());
                }
              }, 100);
            }
          }, 100);
        } catch (error) {
          console.error("Failed to refresh glossary data after update:", error);
        }

        // Document save is now handled by the parent component after block content update
        // No need to call onDocumentSave here as it will be called after onDocumentContentUpdate

        // Call the onUpdate callback if provided
        onUpdate?.(updatedItem);
      }

      // Switch to view mode after save
      setIsManualModeChange(false);
      hasProcessedEditModeRef.current = false;
      setDocumentMode("view");
    } catch (error) {
      console.error("Failed to save document:", error);
      // Error handling is done by the createObject/updateObject functions
      // which show their own toast messages
    }
  };

  /**
   * Cancels the form and resets to view mode
   */
  const handleFormCancel = () => {
    console.log(`[DocumentBlock] Canceling form, switching back to view mode`);
    //documentState.resetForm();
    setIsManualModeChange(false);
    hasProcessedEditModeRef.current = false;
    setDocumentMode("view");
    
    // If this is a new block (no initialData and no content), notify parent to remove it
    if (!initialData && !content && id && onDocumentBlockCancel) {
      onDocumentBlockCancel(id);
    }
  };

  /**
   * Handles blueprint selection change
   */
  const handleBlueprintChange = (blueprintId: string) => {
    if (blueprintId) {
      const blueprintData = filteredBlueprints.find(
        (bp) => bp.id === blueprintId
      );
      if (blueprintData) {
        documentState.updateBlueprint(blueprintData);
      }
    } else {
      documentState.setSelectedBlueprint(null);
      documentState.setSelectedBlueprintId("");
    }
  };

  // ============================================================================
  // RENDER CONDITIONS
  // ============================================================================

  // Get the glossary item data if we're in view mode - memoized to prevent unnecessary calls
  const documentItem = useMemo(
    () => getGlossaryItemFromBlock(),
    [getGlossaryItemFromBlock]
  );

  // Only show form when we have both blueprint and sections - memoized to prevent unnecessary calculations
  const shouldShowForm = useMemo(
    () =>
      documentState.selectedBlueprint &&
      documentState.parsedSections &&
      documentState.parsedSections.length > 0,
    [documentState.selectedBlueprint, documentState.parsedSections]
  );

  // Add debugging for render
  console.log(`[DocumentBlock] Render state:`, {
    documentMode,
    content,
    documentItem: documentItem ? { id: documentItem.id, name: documentItem.name } : null,
    documentItemsLength: documentItems?.length,
    hasSelectedBlueprint: !!documentState.selectedBlueprint,
    hasParsedSections: !!documentState.parsedSections,
    shouldShowForm,
    isManualModeChange,
    hasProcessedEditMode: hasProcessedEditModeRef.current,
    // Add form data debugging
    formData: documentState.formData,
    sectionsData: documentState.parsedSections?.map(section => ({
      id: section.id,
      title: section.title,
      fieldsCount: section.fields?.length || 0,
      fieldValues: section.fields?.map(field => ({ id: field.id, value: field.value })) || []
    })),
    // Add more detailed state debugging
    selectedBlueprintId: documentState.selectedBlueprintId,
    selectedBlueprintName: documentState.selectedBlueprint?.name,
    itemTitle: documentState.itemTitle,
    itemTags: documentState.itemTags,
    itemIdentifiers: documentState.itemIdentifiers
  });



  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="relative w-full h-full min-h-[200px] flex-1">
      {/* Document Block Container */}
      <div className="absolute w-full h-full border border-purple-300 rounded-lg z-0 bg-white" />

      {/* Document Content */}
      <div className="relative w-full h-full rounded-lg z-10 box-border p-6">
        {/* Header Section */}
        <div className="flex-shrink-0 mb-6">
          <UnifiedWizardHeader
            title={documentState.itemTitle}
            mode={documentMode}
            itemType="library"
            onTitleChange={handleTitleChange}
            onCancel={handleFormCancel}
            onSubmit={handleSave}
            isSubmitting={documentState.isSaving}
            submitDisabled={false}
            submitText={
              documentMode === "create" ? "Create Document" : "Update Document"
            }
            hasUnsavedChanges={documentMode !== "view"}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </div>

        {/* Main Content Section */}
        <div className="flex-1">
          {/* Blueprint Selection - Only show when creating/editing */}
          {documentMode !== "view" && (
            <div className="space-y-2 mb-6">
              <label className="block text-sm font-medium text-gray-800">
                Document Template
              </label>
              <div className="relative">
                <select
                  value={documentState.selectedBlueprintId}
                  onChange={(e) => handleBlueprintChange(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none"
                  disabled={isLoading}
                >
                  <option value="" disabled>
                    Select Document Template to create new Document
                  </option>
                  {filteredBlueprints.length > 0 ? (
                    filteredBlueprints.map((blueprint) => (
                      <option key={blueprint.id} value={blueprint.id}>
                        {blueprint.name}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      No document blueprints available
                    </option>
                  )}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          )}

          {/* Form Content - Show when we have required data (only in create/edit mode) */}
          {shouldShowForm && documentMode !== "view" && (
            <div className="mt-6">
              <GlossaryBlockForm
                blueprint={documentState.selectedBlueprint}
                onCancel={handleFormCancel}
                initialData={
                  documentMode === "edit" ? documentItem : initialData
                }
                sections={documentState.parsedSections}
                onSectionsChange={documentState.updateSections}
                mode={documentMode}
              />
            </div>
          )}

          {/* View Mode Display - Show when in view mode and we have form data */}
          {documentMode === "view" && shouldShowForm && (
            <div className="mt-6">
              <GlossaryBlockForm
                blueprint={documentState.selectedBlueprint}
                onCancel={handleFormCancel}
                initialData={initialData || documentItemFromContent}
                sections={documentState.parsedSections}
                onSectionsChange={documentState.updateSections}
                mode={documentMode}
              />
            </div>
          )}

          {/* Loading State - Show when waiting for data */}
          {documentState.selectedBlueprint && !shouldShowForm && (
            <div className="mt-6 flex items-center justify-center p-8">
              <div className="text-gray-500">{t('platform.editor.documentblock.preparing_form')}</div>
            </div>
          )}

          {/* Loading State for View Mode - Show when waiting for data */}
          {documentMode === "view" && !shouldShowForm && (
            <div className="mt-6 flex flex-col items-center justify-center p-8 space-y-4">
              <div className="text-gray-500">{t('platform.editor.documentblock.loading_document_data')}</div>
              <button
                onClick={async () => {
                  console.log(`[DocumentBlock] Manual refresh triggered`);
                  try {
                    await loadDocumentData();
                  } catch (error) {
                    console.error("Manual refresh failed:", error);
                  }
                }}
                className="px-4 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
              >
                Refresh Data
              </button>
            </div>
          )}

          {/* Show empty state when no blueprint selected */}
          {!documentState.selectedBlueprint && documentMode !== "view" && (
            <div className="mt-6 flex items-center justify-center p-8">
              {filteredBlueprints.length > 0 ? (
                <div className="text-gray-500">
                  Select a document blueprint to get started
                </div>
              ) : (
                <div className="text-center">
                  <div className="text-gray-500 mb-2">
                    No document blueprints available
                  </div>
                  <div className="text-sm text-gray-400">
                    Create a document blueprint first to use this block
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
